/* global RequestInit */
const API_BASE = import.meta.env.VITE_API_URL || "/api";

function getToken(): string | null {
  return localStorage.getItem("ksm_token");
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = { ...(options.headers as Record<string, string>) };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (!(options.body instanceof FormData)) {
    headers["Content-Type"] = headers["Content-Type"] || "application/json";
  }

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || "Error de servidor");
  }
  return res.json();
}

export interface AuthUser {
  id: string;
  full_name: string;
  email: string;
  role: "super_admin" | "admin" | "marketing" | "instructor" | "evaluador" | "estudiante";
  company: string;
  position: string;
  phone: string;
  can_evaluate: boolean;
  active?: boolean;
  signature_url?: string | null;
  created_at?: string;
  credit_balance: number;
  deleted_at?: string | null;
  password_changed_at?: string | null;
}

export interface CourseEdition {
  id: string;
  course_id: string;
  edition_label: string;
  start_date: string;
  end_date: string;
  location: string;
  instructor_id: string | null;
  evaluator_id: string | null;
  capacity: number;
  price: number;
  status: string;
  course?: Course;
  instructor?: { id: string; full_name: string; email?: string; signature_url?: string | null };
  evaluator?: { id: string; full_name: string; email?: string; signature_url?: string | null };
  enrollments?: Enrollment[];
  _count?: { enrollments: number };
  service_type?: "course" | "diplomado";
}

export interface Course {
  id: string;
  name: string;
  description: string;
  detail_title: string;
  modality: string;
  target_audience: string;
  highlights: string[];
  resource_files: CourseResource[];
  duration: string;
  hours: number;
  category: string;
  image_url: string;
  show_on_landing: boolean;
  active: boolean;
  deleted_at?: string | null;
  editions?: CourseEdition[];
  _count?: { editions: number };
}

export interface CourseResource {
  name: string;
  url: string;
}

export interface DiplomadoEdition {
  id: string;
  diplomado_id: string;
  edition_label: string;
  start_date: string;
  end_date: string;
  location: string;
  instructor_id: string | null;
  evaluator_id: string | null;
  capacity: number;
  price: number;
  status: string;
  diplomado?: Diplomado;
  instructor?: { id: string; full_name: string; email?: string; signature_url?: string | null };
  evaluator?: { id: string; full_name: string; email?: string; signature_url?: string | null };
  _count?: { enrollments: number };
}

export interface Diplomado {
  id: string;
  name: string;
  description: string;
  detail_title: string;
  modality: string;
  target_audience: string;
  highlights: string[];
  resource_files: CourseResource[];
  duration: string;
  hours: number;
  category: string;
  image_url: string;
  show_on_landing: boolean;
  active: boolean;
  deleted_at?: string | null;
  editions?: DiplomadoEdition[];
  _count?: { editions: number };
}

export interface Enrollment {
  id: string;
  student_id: string;
  course_id?: string | null;
  edition_id?: string | null;
  diplomado_id?: string | null;
  diplomado_edition_id?: string | null;
  registration_date: string;
  emergency_contact: string;
  emergency_phone: string;
  enrollment_status: "pendiente" | "aceptada" | "rechazada";
  payment_status: "pendiente" | "abono_parcial" | "pagado" | "rechazado" | "cortesia";
  payment_amount: number;
  payment_method: string;
  payment_reference: string;
  payment_proof_url?: string | null;
  attendance_status: string;
  evaluation_status: string;
  certificate_issued: boolean;
  certificate_id?: string | null;
  observations: string;
  evaluator_notes: string;
  payments?: Payment[];
  student?: { id: string; full_name: string; email: string; company?: string; phone?: string };
  edition?: CourseEdition & { course?: Course };
  diplomadoEdition?: DiplomadoEdition & { diplomado?: Diplomado };
  certificate?: Certificate | null;
}

export interface Payment {
  id: string;
  receipt_number: string;
  enrollment_id: string;
  student_id: string;
  course_id?: string | null;
  edition_id?: string | null;
  diplomado_id?: string | null;
  diplomado_edition_id?: string | null;
  amount: number;
  payment_method: string;
  payment_date: string;
  reference: string;
  proof_url?: string | null;
  status: string;
  notes?: string;
  student?: { id: string; full_name: string; email: string; company?: string };
  edition?: CourseEdition & { course?: { id: string; name: string; category?: string } };
  diplomadoEdition?: DiplomadoEdition & { diplomado?: { id: string; name: string; category?: string } };
  enrollment?: Enrollment;
}

export interface PendingPayment {
  enrollment_id: string;
  student: { id: string; full_name: string; email: string; company?: string };
  course_id: string;
  course_name: string;
  edition_label: string;
  edition_id: string;
  service_type?: "course" | "diplomado";
  course_price: number;
  amount_paid: number;
  amount_remaining: number;
  payment_reference: string;
  payment_status: string;
  enrollment_status: string;
  registration_date: string;
}

export interface Certificate {
  id: string;
  certificate_number: string;
  student_id: string;
  course_id?: string | null;
  edition_id?: string | null;
  diplomado_id?: string | null;
  diplomado_edition_id?: string | null;
  issue_date: string;
  hours_completed: number;
  qr_code: string;
  instructor_signature: string;
  evaluator_signature: string;
  status: string;
  student?: { id: string; full_name: string; email: string; company?: string; position?: string; phone?: string };
  edition?: CourseEdition & { course?: Course };
  diplomadoEdition?: DiplomadoEdition & { diplomado?: Diplomado };
}

export interface AuditLog {
  id: string;
  user_name: string;
  action: string;
  entity_type: string;
  entity_id: string;
  details: string;
  created_at: string;
}

export interface DashboardStats {
  activeCourses: number;
  activeEditions: number;
  catalogCourses: number;
  totalStudents: number;
  totalCertificates: number;
  pendingEvaluations: number;
  pendingEnrollments: number;
  collected: number;
  pending: number;
  pendingBalancesCount: number;
  smtpConfigured: boolean;
  skippedEmails: number;
  courseId?: string | null;
  editionId?: string | null;
  instructorId?: string | null;
  month?: string | null;
  dateFrom?: string | null;
  dateTo?: string | null;
}

export interface DashboardStatsFilters {
  courseId?: string;
  editionId?: string;
  instructorId?: string;
  category?: string;
  month?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface PublicSiteSettings {
  logo_full: string;
  logo_icon: string;
  logo_dark: string;
  favicon: string;
  hero_home: string;
  hero_login: string;
  hero_validate: string;
  course_default: string;
  og_image: string;
  certificate_logo: string;
  certificate_stamp: string;
  certificate_stamp_size: string;
  landing_about: string;
  landing_impact: string;
  landing_careers: string;
  landing_contact: string;
  site_name: string;
  site_tagline: string;
  hero_title: string;
  hero_title_accent: string;
  hero_description: string;
  meta_title: string;
  meta_description: string;
  meta_keywords: string;
  contact_email: string;
  contact_phone: string;
  contact_address: string;
  contact_secondary_email: string;
  social_facebook: string;
  social_instagram: string;
  social_linkedin: string;
  site_url: string;
  updated_at: string;
}

export interface ContactInquiry {
  id: string;
  type: "b2b" | "b2c";
  name: string;
  email: string;
  phone: string;
  company: string;
  position: string;
  message: string;
  created_at: string;
}

export interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  body_html: string;
  category: string;
  active: boolean;
}

export interface MarketingCampaign {
  id: string;
  name: string;
  template_id: string | null;
  subject: string;
  body_html: string;
  status: string;
  sent_count: number;
  sent_at: string | null;
  template?: EmailTemplate | null;
}

export interface EmailLog {
  id: string;
  to_email: string;
  subject: string;
  type: string;
  status: string;
  created_at: string;
}

export interface NewsPost {
  id: string;
  title: string;
  category: string;
  summary: string;
  content: string;
  image_url: string;
  published: boolean;
  published_at: string;
  created_at: string;
  updated_at: string;
}

export interface Partner {
  id: string;
  name: string;
  logo_url: string;
  active: boolean;
  created_at: string;
}

export interface UpdatePasswordData {
  current_password?: string;
  new_password?: string;
}

export interface ResetPasswordData {
  email: string;
  token: string;
  new_password?: string;
}

function normalizeDiplomadoEdition(edition: DiplomadoEdition): CourseEdition {
  return {
    id: edition.id,
    course_id: edition.diplomado_id,
    edition_label: edition.edition_label,
    start_date: edition.start_date,
    end_date: edition.end_date,
    location: edition.location,
    instructor_id: edition.instructor_id,
    evaluator_id: edition.evaluator_id,
    capacity: edition.capacity,
    price: edition.price,
    status: edition.status,
    course: edition.diplomado as unknown as Course,
    instructor: edition.instructor,
    evaluator: edition.evaluator,
    service_type: "diplomado",
  };
}

export const api = {
  login: (email: string, password: string) =>
    request<{ token: string; user: AuthUser }>("/auth/login", { method: "POST", body: JSON.stringify({ email, password }) }),
  me: () => request<AuthUser>("/auth/me"),
  requestPasswordReset: (email: string) => request<{ ok: boolean }>("/auth/forgot-password", { method: "POST", body: JSON.stringify({ email }) }),
  resetPassword: (data: ResetPasswordData) => request<{ ok: boolean }>("/auth/reset-password", { method: "POST", body: JSON.stringify(data) }),
  changePassword: (current_password: string, new_password: string) =>
    request<{ ok: boolean }>("/auth/password", { method: "PATCH", body: JSON.stringify({ current_password, new_password }) }),
  updateProfile: (data: { full_name?: string; company?: string; phone?: string; position?: string }) =>
    request<AuthUser>("/auth/profile", { method: "PATCH", body: JSON.stringify(data) }),

  getCourses: (options?: { includeDeleted?: boolean }) => request<Course[]>(options?.includeDeleted ? "/courses?includeDeleted=true" : "/courses"),
  getPublicCourses: () => request<Course[]>("/courses/public"),
  getPublicCourse: (id: string) => request<Course>(`/courses/public/${id}`),
  getCourse: (id: string) => request<Course>(`/courses/${id}`),
  createCourse: (data: Partial<Course>) => request<Course>("/courses", { method: "POST", body: JSON.stringify(data) }),
  updateCourse: (id: string, data: Partial<Course>) => request<Course>(`/courses/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  uploadCourseImage: (id: string, file: File) => {
    const fd = new FormData();
    fd.append("image", file);
    return request<Course>(`/courses/${id}/image`, { method: "POST", body: fd, headers: {} });
  },
  uploadCourseResource: (id: string, file: File, name?: string) => {
    const fd = new FormData();
    fd.append("file", file);
    if (name) fd.append("name", name);
    return request<Course>(`/courses/${id}/resource`, { method: "POST", body: fd, headers: {} });
  },
  deleteCourse: (id: string) => request<{ ok: boolean }>(`/courses/${id}`, { method: "DELETE" }),
  restoreCourse: (id: string) => request<Course>(`/courses/${id}/restore`, { method: "POST" }),

  getDiplomados: (options?: { includeDeleted?: boolean }) => request<Diplomado[]>(options?.includeDeleted ? "/diplomados?includeDeleted=true" : "/diplomados"),
  getPublicDiplomados: () => request<Diplomado[]>("/diplomados/public"),
  getPublicDiplomado: (id: string) => request<Diplomado>(`/diplomados/public/${id}`),
  getDiplomado: (id: string) => request<Diplomado>(`/diplomados/${id}`),
  createDiplomado: (data: Partial<Diplomado>) => request<Diplomado>("/diplomados", { method: "POST", body: JSON.stringify(data) }),
  updateDiplomado: (id: string, data: Partial<Diplomado>) => request<Diplomado>(`/diplomados/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  uploadDiplomadoImage: (id: string, file: File) => {
    const fd = new FormData();
    fd.append("image", file);
    return request<Diplomado>(`/diplomados/${id}/image`, { method: "POST", body: fd, headers: {} });
  },
  uploadDiplomadoResource: (id: string, file: File, name?: string) => {
    const fd = new FormData();
    fd.append("file", file);
    if (name) fd.append("name", name);
    return request<Diplomado>(`/diplomados/${id}/resource`, { method: "POST", body: fd, headers: {} });
  },
  deleteDiplomado: (id: string) => request<{ ok: boolean }>(`/diplomados/${id}`, { method: "DELETE" }),
  restoreDiplomado: (id: string) => request<Diplomado>(`/diplomados/${id}/restore`, { method: "POST" }),

  getEditions: async () => {
    const [courseEditions, diplomadoEditions] = await Promise.all([
      request<CourseEdition[]>("/editions"),
      request<DiplomadoEdition[]>("/diplomado-editions").catch(() => []),
    ]);
    return [
      ...courseEditions.map((edition) => ({ ...edition, service_type: "course" as const })),
      ...diplomadoEditions.map(normalizeDiplomadoEdition),
    ].sort((a, b) => a.start_date.localeCompare(b.start_date));
  },
  getEdition: async (id: string) => {
    try {
      const edition = await request<CourseEdition>(`/editions/${id}`);
      return { ...edition, service_type: "course" as const };
    } catch {
      const edition = normalizeDiplomadoEdition(await request<DiplomadoEdition>(`/diplomado-editions/${id}`));
      const enrollments = await request<Enrollment[]>("/enrollments").catch(() => []);
      return { ...edition, enrollments: enrollments.filter((e) => e.diplomado_edition_id === id) };
    }
  },
  createEdition: (data: Partial<CourseEdition> & { course_id: string }) =>
    request<CourseEdition>("/editions", { method: "POST", body: JSON.stringify(data) }),
  updateEdition: (id: string, data: Partial<CourseEdition>) =>
    request<CourseEdition>(`/editions/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  deleteEdition: (id: string) => request<{ ok: boolean }>(`/editions/${id}`, { method: "DELETE" }),

  getDiplomadoEditions: () => request<DiplomadoEdition[]>("/diplomado-editions"),
  getDiplomadoEdition: (id: string) => request<DiplomadoEdition>(`/diplomado-editions/${id}`),
  createDiplomadoEdition: (data: Partial<DiplomadoEdition> & { diplomado_id: string }) =>
    request<DiplomadoEdition>("/diplomado-editions", { method: "POST", body: JSON.stringify(data) }),
  updateDiplomadoEdition: (id: string, data: Partial<DiplomadoEdition>) =>
    request<DiplomadoEdition>(`/diplomado-editions/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  deleteDiplomadoEdition: (id: string) => request<{ ok: boolean }>(`/diplomado-editions/${id}`, { method: "DELETE" }),

  addWalletCredit: async (userId: string, data: FormData) => {
    const res = await fetch(`/api/users/${userId}/wallet`, {
      method: "POST",
      headers: { Authorization: `Bearer ${getToken()}` },
      body: data,
    });
    if (!res.ok) throw await res.json();
    return res.json();
  },

  getEnrollments: () => request<Enrollment[]>("/enrollments"),
  publicEnrollment: (data: FormData) =>
    request<Enrollment & { is_new_user?: boolean }>("/enrollments/public", { method: "POST", body: data, headers: {} }),
  adminEnrollment: (data: { student_id: string; edition_id?: string; diplomado_edition_id?: string; courtesy?: boolean }) =>
    request<Enrollment>("/enrollments/admin", { method: "POST", body: JSON.stringify(data) }),
  markEnrollmentCourtesy: (id: string) =>
    request<Enrollment>(`/enrollments/${id}/courtesy`, { method: "PATCH" }),
  acceptEnrollment: (id: string) => request<Enrollment>(`/enrollments/${id}/accept`, { method: "PATCH" }),
  rejectEnrollment: (id: string) => request<Enrollment>(`/enrollments/${id}/reject`, { method: "PATCH" }),
  updateAttendance: (id: string, data: { attendance_status: string; observations?: string }) =>
    request<Enrollment>(`/enrollments/${id}/attendance`, { method: "PATCH", body: JSON.stringify(data) }),
  updateEvaluation: (id: string, data: { evaluation_status: string; evaluator_notes?: string }) =>
    request<Enrollment>(`/enrollments/${id}/evaluation`, { method: "PATCH", body: JSON.stringify(data) }),
  deleteEnrollment: async (id: string) => {
    const res = await fetch(`${API_BASE}/enrollments/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (!res.ok) throw await res.json();
    return res.json();
  },

  getPayments: () => request<Payment[]>("/payments"),
  getPendingPayments: () => request<PendingPayment[]>("/payments/pending"),
  submitPayment: (formData: FormData) => request<Payment>("/payments/submit", { method: "POST", body: formData, headers: {} }),
  updatePaymentStatus: (id: string, status: string) =>
    request<Payment>(`/payments/${id}/status`, { method: "PATCH", body: JSON.stringify({ status }) }),
  deletePayment: async (id: string) => {
    const res = await fetch(`${API_BASE}/payments/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (!res.ok) throw await res.json();
    return res.json();
  },

  getCertificates: () => request<Certificate[]>("/certificates"),
  validateCertificate: (number: string) =>
    request<{
      valid: boolean;
      certificate_number?: string;
      student_name?: string;
      company?: string;
      course_name?: string;
      course_category?: string;
      course_duration?: string;
      hours?: number;
      issue_date?: string;
      instructor_signature?: string;
      evaluator_signature?: string;
      qr_code?: string;
      error?: string;
    }>(`/certificates/validate/${number}`),
  issueCertificate: (enrollment_id: string) =>
    request<Certificate>("/certificates/issue", { method: "POST", body: JSON.stringify({ enrollment_id }) }),
  resendCertificate: (id: string) => request<{ ok: boolean }>(`/certificates/${id}/resend`, { method: "POST" }),
  revokeCertificate: (id: string) => request<Certificate>(`/certificates/${id}/revoke`, { method: "PATCH" }),
  downloadCertificatePdf: async (id: string) => {
    const token = getToken();
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(`${API_BASE}/certificates/${id}/pdf`, { headers });
    if (!res.ok) throw new Error("Error al descargar PDF");
    const blob = await res.blob();
    return blob;
  },
  downloadPublicCertificatePdf: async (number: string) => {
    const res = await fetch(`${API_BASE}/certificates/validate/${number}/pdf`);
    if (!res.ok) throw new Error("Error al descargar PDF");
    const blob = await res.blob();
    return blob;
  },

  submitContact: (data: Record<string, string>) =>
    request<{ ok: boolean }>("/contacts", { method: "POST", body: JSON.stringify(data) }),
  getContacts: () => request<ContactInquiry[]>("/contacts"),

  getDashboardStats: (filters?: DashboardStatsFilters | string) => {
    const params = new URLSearchParams();
    if (typeof filters === "string") {
      if (filters) params.set("courseId", filters);
    } else if (filters) {
      if (filters.courseId) params.set("courseId", filters.courseId);
      if (filters.editionId) params.set("editionId", filters.editionId);
      if (filters.instructorId) params.set("instructorId", filters.instructorId);
      if (filters.category) params.set("category", filters.category);
      if (filters.month) params.set("month", filters.month);
      if (filters.dateFrom) params.set("dateFrom", filters.dateFrom);
      if (filters.dateTo) params.set("dateTo", filters.dateTo);
    }
    const qs = params.toString();
    return request<DashboardStats>(qs ? `/users/dashboard/stats?${qs}` : "/users/dashboard/stats");
  },
  getUsers: (options?: { includeDeleted?: boolean }) =>
    request<AuthUser[]>(options?.includeDeleted ? "/users?includeDeleted=true" : "/users"),
  createUser: (data: Record<string, unknown>) =>
    request<AuthUser>("/users", { method: "POST", body: JSON.stringify(data) }),
  updateUser: (id: string, data: Record<string, unknown>) =>
    request<AuthUser>(`/users/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  deleteUser: (id: string) => request<{ ok: boolean; deleted_at: string }>(`/users/${id}`, { method: "DELETE" }),
  restoreUser: (id: string) => request<AuthUser>(`/users/${id}/restore`, { method: "POST" }),
  resendTempPassword: (id: string) => request<{ ok: boolean; email_sent: boolean }>(`/users/${id}/resend-temp-password`, { method: "POST" }),
  getInstructors: () => request<AuthUser[]>("/users/instructors"),
  uploadSignature: (file: File) => {
    const fd = new FormData();
    fd.append("signature", file);
    return request<{ signature_url: string }>("/users/signature", { method: "POST", body: fd, headers: {} });
  },

  getAuditLogs: () => request<AuditLog[]>("/audit-logs"),
  getTemplates: () => request<EmailTemplate[]>("/marketing/templates"),
  createTemplate: (data: Partial<EmailTemplate>) =>
    request<EmailTemplate>("/marketing/templates", { method: "POST", body: JSON.stringify(data) }),
  updateTemplate: (id: string, data: Partial<EmailTemplate>) =>
    request<EmailTemplate>(`/marketing/templates/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  deleteTemplate: (id: string) =>
    request<{ ok: boolean }>(`/marketing/templates/${id}`, { method: "DELETE" }),
  getCampaigns: () => request<MarketingCampaign[]>("/marketing/campaigns"),
  createCampaign: (data: Partial<MarketingCampaign>) =>
    request<MarketingCampaign>("/marketing/campaigns", { method: "POST", body: JSON.stringify(data) }),
  sendCampaign: (id: string, data: { audience: string; edition_id?: string }) =>
    request<{ sent: number; total: number }>(`/marketing/campaigns/${id}/send`, { method: "POST", body: JSON.stringify(data) }),
  sendTestEmail: (data: { to: string; subject: string; body_html: string }) =>
    request<{ ok: boolean }>("/marketing/test", { method: "POST", body: JSON.stringify(data) }),
  getEmailLogs: () => request<EmailLog[]>("/marketing/email-logs"),

  getPublicSettings: () => request<PublicSiteSettings>("/settings/public"),
  getSettings: () => request<Record<string, unknown>>("/settings"),
  updateSettings: (data: Partial<PublicSiteSettings>) =>
    request<PublicSiteSettings>("/settings", { method: "PATCH", body: JSON.stringify(data) }),
  uploadBrandingAsset: (asset: string, file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return request<PublicSiteSettings>(`/settings/upload/${asset}`, { method: "POST", body: fd, headers: {} });
  },
  clearBrandingAsset: (asset: string) =>
    request<PublicSiteSettings>(`/settings/upload/${asset}`, { method: "DELETE" }),

  getPublicNews: () => request<NewsPost[]>("/news/public"),
  getNews: () => request<NewsPost[]>("/news"),
  createNews: (data: Partial<NewsPost>) =>
    request<NewsPost>("/news", { method: "POST", body: JSON.stringify(data) }),
  updateNews: (id: string, data: Partial<NewsPost>) =>
    request<NewsPost>(`/news/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  deleteNews: (id: string) => request<{ ok: boolean }>(`/news/${id}`, { method: "DELETE" }),
  uploadNewsImage: (id: string, file: File) => {
    const fd = new FormData();
    fd.append("image", file);
    return request<NewsPost>(`/news/${id}/image`, { method: "POST", body: fd, headers: {} });
  },

  getPublicPartners: () => request<Partner[]>("/partners/public"),
  getPartners: () => request<Partner[]>("/partners"),
  createPartner: (data: Partial<Partner>) => request<Partner>("/partners", { method: "POST", body: JSON.stringify(data) }),
  updatePartner: (id: string, data: Partial<Partner>) => request<Partner>(`/partners/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
  deletePartner: (id: string) => request<{ ok: boolean }>(`/partners/${id}`, { method: "DELETE" }),
  uploadPartnerLogo: (id: string, file: File) => {
    const fd = new FormData();
    fd.append("image", file);
    return request<Partner>(`/partners/${id}/logo`, { method: "POST", body: fd, headers: {} });
  },
};

export function setAuthToken(token: string | null) {
  if (token) localStorage.setItem("ksm_token", token);
  else localStorage.removeItem("ksm_token");
}

export function formatDate(d: string) {
  const date = new Date(d);
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const year = date.getUTCFullYear();
  return `${day}/${month}/${year}`;
}

export function courseImageUrl(url?: string, fallback?: string) {
  if (url) return url.startsWith("/") ? `${API_BASE.replace("/api", "")}${url}` : url;
  return fallback || "/assets/course-default.svg";
}

export function assetUrl(url?: string | null) {
  if (!url) return "";
  return url.startsWith("/") ? `${API_BASE.replace("/api", "")}${url}` : url;
}

export function enrollmentBalance(
  enrollment: Pick<Enrollment, "payment_amount" | "payment_status"> & {
    edition?: Pick<CourseEdition, "price"> | null;
    diplomadoEdition?: Pick<DiplomadoEdition, "price"> | null;
  }
) {
  const isCourtesy = enrollment.payment_status === "cortesia";
  const coursePrice = enrollment.edition?.price ?? enrollment.diplomadoEdition?.price ?? 0;
  const amountPaid = isCourtesy ? 0 : enrollment.payment_amount ?? 0;
  const amountRemaining = isCourtesy
    ? 0
    : Math.max(0, Math.round((coursePrice - amountPaid) * 100) / 100);
  return {
    coursePrice,
    amountPaid,
    amountRemaining,
    isFullyPaid: isCourtesy || coursePrice <= 0 || amountRemaining <= 0.009,
    isCourtesy,
  };
}
